import CellPrice from '@/components/GokuDataGrid/CellPrice'
import { formatNumeral } from '@/utils/function'
import { useTranslation } from 'react-i18next'
import { CustomerRevenueCombinedSummary } from '../../../services'

interface StatisticsCardsProps {
    statistics: {
        totalRevenue: number
        actualRevenue: number
        projectedRevenue: number
        paidAmount: number
        unpaidAmount: number
    }
    summary?: CustomerRevenueCombinedSummary
}

type ColorVariant = 'blue' | 'green' | 'amber' | 'teal' | 'red'

const colorClasses: Record<
    ColorVariant,
    { bg: string; border: string; text: string }
> = {
    blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
    },
    green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
    },
    amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
    },
    teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-700',
    },
    red: {
        bg: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-700',
    },
}

interface StatCardProps {
    title: string
    value: number
    recordsCount?: number
    colorVariant: ColorVariant
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    recordsCount,
    colorVariant,
}) => {
    const { t } = useTranslation('customer')
    const colors = colorClasses[colorVariant]

    return (
        <div className={`${colors.bg} border ${colors.border} rounded-md p-4`}>
            <div className="text-sm text-gray-600 mb-1">{title}</div>
            <div className={`text-xl font-bold ${colors.text}`}>
                <CellPrice price={value} />
            </div>
            {recordsCount !== undefined && (
                <div className="text-xs text-gray-500 mt-1">
                    {formatNumeral(recordsCount, 0)} {t('records')}
                </div>
            )}
        </div>
    )
}

const StatisticsCards: React.FC<StatisticsCardsProps> = ({
    statistics,
    summary,
}) => {
    const { t } = useTranslation('customer')

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-2">
            <StatCard
                title={t('total_revenue')}
                value={statistics.totalRevenue}
                recordsCount={summary?.total_records}
                colorVariant="blue"
            />
            <StatCard
                title={t('actual_revenue')}
                value={statistics.actualRevenue}
                recordsCount={summary?.total_actual_records}
                colorVariant="green"
            />
            <StatCard
                title={t('projected_revenue')}
                value={statistics.projectedRevenue}
                recordsCount={summary?.total_projected_records}
                colorVariant="amber"
            />
            <StatCard
                title={t('paid_amount')}
                value={statistics.paidAmount}
                recordsCount={summary?.total_paid_records}
                colorVariant="teal"
            />
            <StatCard
                title={t('unpaid_amount')}
                value={statistics.unpaidAmount}
                recordsCount={summary?.total_unpaid_records}
                colorVariant="red"
            />
        </div>
    )
}

export default StatisticsCards
